import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';

const TireView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pneu, setPneu] = useState<any>(null);
  const [analise, setAnalise] = useState<any>(null);
  const [medicoes, setMedicoes] = useState<any[]>([]);
  const [movimentacoes, setMovimentacoes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (id) loadData(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setIsLoading(true);
      const pneuId = parseInt(id!);

      // Carregar pneu primeiro (obrigatório)
      const pneuRes = await apiService.getPneu(pneuId);
      if (!pneuRes.success || !pneuRes.data) {
        setError('Pneu nao encontrado');
        return;
      }
      setPneu(pneuRes.data);

      // Carregar dados complementares independentemente (opcionais)
      const [analiseRes, medicoesRes, movRes] = await Promise.allSettled([
        apiService.getPneuAnalise(pneuId),
        apiService.getPneuMedicoes(pneuId),
        apiService.getPneuMovimentacoes(pneuId)
      ]);

      if (analiseRes.status === 'fulfilled' && analiseRes.value.success && analiseRes.value.data) {
        setAnalise(analiseRes.value.data);
      }
      if (medicoesRes.status === 'fulfilled' && medicoesRes.value.success && medicoesRes.value.data) {
        setMedicoes(Array.isArray(medicoesRes.value.data) ? medicoesRes.value.data : []);
      }
      if (movRes.status === 'fulfilled' && movRes.value.success && movRes.value.data) {
        setMovimentacoes(Array.isArray(movRes.value.data) ? movRes.value.data : []);
      }
    } catch (err: any) {
      setError('Erro ao carregar dados do pneu');
      utils.showNotification(err.message || 'Erro ao carregar pneu', { type: 'error' });
    } finally { setIsLoading(false); }
  };

  const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';
  const formatCurrency = (v: number) => v ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : '-';

  const getStatusLabel = (s: string): string => {
    const map: Record<string, string> = { 'novo': 'Novo', 'em_uso': 'Em Uso', 'estoque': 'Estoque', 'recapagem': 'Recapagem', 'descartado': 'Descartado' };
    return map[s] || s;
  };

  const getMovTipoLabel = (t: string): string => {
    const map: Record<string, string> = { 'instalacao': 'Instalacao', 'rodizio': 'Rodizio', 'remocao': 'Remocao', 'recapagem': 'Recapagem', 'descarte': 'Descarte' };
    return map[t] || t;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando dados do pneu...</p>
        </div>
      </div>
    );
  }

  if (error || !pneu) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <h3>{error || 'Pneu nao encontrado'}</h3>
          <button className="btn btn-primary" onClick={() => navigate('/pneus')}>Voltar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Pneu #{pneu.id} - {pneu.marca} {pneu.modelo}</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/pneus')}>
          <i className="fas fa-arrow-left"></i> Voltar
        </button>
      </div>

      <div className="page-content">
        {/* Dados do pneu */}
        <div className="card">
          <div className="card-header"><h2>Dados do Pneu</h2></div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item"><label>Marca:</label><span className="info-value">{pneu.marca}</span></div>
              <div className="info-item"><label>Modelo:</label><span className="info-value">{pneu.modelo || '-'}</span></div>
              <div className="info-item"><label>Medida:</label><span className="info-value">{pneu.medida}</span></div>
              <div className="info-item"><label>DOT:</label><span className="info-value">{pneu.dot || '-'}</span></div>
              <div className="info-item"><label>N. Fogo:</label><span className="info-value">{pneu.numero_fogo || '-'}</span></div>
              <div className="info-item"><label>Veiculo:</label><span className="info-value">{pneu.veiculo_placa || 'Nenhum'}</span></div>
              <div className="info-item"><label>Posicao:</label><span className="info-value">{pneu.posicao || '-'}</span></div>
              <div className="info-item"><label>Status:</label><span className="info-value">{getStatusLabel(pneu.status)}</span></div>
              <div className="info-item"><label>Valor:</label><span className="info-value">{formatCurrency(pneu.valor_unitario)}</span></div>
              <div className="info-item"><label>Fornecedor:</label><span className="info-value">{pneu.fornecedor || '-'}</span></div>
              <div className="info-item"><label>Recapagens:</label><span className="info-value">{pneu.recapagens || 0} / {pneu.max_recapagens || 3}</span></div>
              <div className="info-item"><label>KM Total:</label><span className="info-value">{new Intl.NumberFormat('pt-BR').format(pneu.quilometragem_total || 0)} km</span></div>
            </div>
          </div>
        </div>

        {/* Analise de consumo */}
        {analise && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header"><h2>Analise de Consumo e Previsibilidade</h2></div>
            <div className="card-body">
              <div className="dashboard-grid">
                <div className="stat-card">
                  <div className="stat-icon"><i className="fas fa-ruler-vertical"></i></div>
                  <div className="stat-value">{analise.sulco_atual} mm</div>
                  <div className="stat-label">Sulco Atual</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><i className="fas fa-chart-line"></i></div>
                  <div className="stat-value">{analise.desgaste_total_mm} mm</div>
                  <div className="stat-label">Desgaste Total</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><i className="fas fa-tachometer-alt"></i></div>
                  <div className="stat-value">{analise.consumo_mm_por_1000km ? `${analise.consumo_mm_por_1000km} mm` : '-'}</div>
                  <div className="stat-label">Consumo / 1000 km</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><i className="fas fa-road"></i></div>
                  <div className="stat-value">{analise.vida_util_restante_km ? `${new Intl.NumberFormat('pt-BR').format(analise.vida_util_restante_km)} km` : '-'}</div>
                  <div className="stat-label">Vida Util Restante</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><i className="fas fa-calendar-alt"></i></div>
                  <div className="stat-value">{analise.dias_restantes ? `${analise.dias_restantes} dias` : '-'}</div>
                  <div className="stat-label">Dias Restantes</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><i className="fas fa-exchange-alt"></i></div>
                  <div className="stat-value">{analise.previsao_troca ? formatDate(analise.previsao_troca) : '-'}</div>
                  <div className="stat-label">Previsao de Troca</div>
                </div>
              </div>
              {analise.mensagem && (
                <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px', color: '#7f8c8d' }}>
                  <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>{analise.mensagem}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Historico de medicoes */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header"><h2>Historico de Medicoes ({medicoes.length})</h2></div>
          <div className="card-body">
            {medicoes.length === 0 ? (
              <p style={{ color: '#7f8c8d' }}>Nenhuma medicao registrada.</p>
            ) : (
              <table className="data-table">
                <thead><tr><th>Data</th><th>Sulco (mm)</th><th>KM Veiculo</th><th>Posicao</th><th>Obs</th></tr></thead>
                <tbody>
                  {medicoes.map((m: any) => (
                    <tr key={m.id}>
                      <td>{formatDate(m.data_medicao)}</td>
                      <td><strong>{m.sulco_medido} mm</strong></td>
                      <td>{m.quilometragem_veiculo ? new Intl.NumberFormat('pt-BR').format(m.quilometragem_veiculo) : '-'}</td>
                      <td>{m.posicao || '-'}</td>
                      <td>{m.observacoes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Historico de movimentacoes */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header"><h2>Historico de Movimentacoes ({movimentacoes.length})</h2></div>
          <div className="card-body">
            {movimentacoes.length === 0 ? (
              <p style={{ color: '#7f8c8d' }}>Nenhuma movimentacao registrada.</p>
            ) : (
              <table className="data-table">
                <thead><tr><th>Data</th><th>Tipo</th><th>Origem</th><th>Destino</th><th>Sulco</th><th>Motivo</th></tr></thead>
                <tbody>
                  {movimentacoes.map((mv: any) => (
                    <tr key={mv.id}>
                      <td>{formatDate(mv.data_movimentacao)}</td>
                      <td><span className="status-badge status-info">{getMovTipoLabel(mv.tipo)}</span></td>
                      <td>{mv.veiculo_origem_placa ? `${mv.veiculo_origem_placa} (${mv.posicao_origem || '-'})` : '-'}</td>
                      <td>{mv.veiculo_destino_placa ? `${mv.veiculo_destino_placa} (${mv.posicao_destino || '-'})` : '-'}</td>
                      <td>{mv.sulco_no_momento ? `${mv.sulco_no_momento} mm` : '-'}</td>
                      <td>{mv.motivo || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TireView;
